import type { BaseRange } from 'slate';
import { Editor, Element, Node, Path, Point, Range, Text } from 'slate';

import { flatMapObjectValues, isEvenNum, isOddNum } from '../../util';
import {
  isFooterElement,
  isHeaderElement,
  isInlineInstructionFieldElement,
  isSelectionIntersection,
  Queries,
} from '../internal';
import type { FooterType, HeaderType, SelectionIntersection } from '../internal';
import { isInvisibleElement, isParagraphElement, isTableCellElement } from '../types';

export const selectionQueries = {
  /**
   * Clamp a candidate point into the table cell containing the reference point. Used to keep a
   * word/line drag-expansion from spilling past a cell's content into the next cell (the next
   * column). A reference point outside any table returns the candidate unchanged.
   */
  clampPointToTableCell(
    editor: Editor,
    options: { candidate: Point | undefined; referencePoint: Point }
  ): Point {
    const { candidate, referencePoint } = options;

    if (!candidate) return referencePoint;

    const cellEntry = Editor.above(editor, {
      at: referencePoint,
      match: isTableCellElement,
      mode: 'lowest',
    });
    if (!cellEntry) return candidate;

    const [, cellPath] = cellEntry;
    const [cellStart, cellEnd] = Editor.edges(editor, cellPath);
    if (Point.isBefore(candidate, cellStart)) return cellStart;
    if (Point.isAfter(candidate, cellEnd)) return cellEnd;

    return candidate;
  },

  /*
   * Get the footer type above the selection
   */
  getFooterTypeAboveSelection(editor: Editor): FooterType | null {
    const selectionBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor);
    if (!selectionBoundaryEntry) return null;

    const [selectionBoundaryNode] = selectionBoundaryEntry;
    if (!isFooterElement(selectionBoundaryNode)) return null;

    return selectionBoundaryNode.type;
  },

  /**
   * Get the header type above the selection
   */
  getHeaderTypeAboveSelection(editor: Editor): HeaderType | null {
    const selectionBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor);
    if (!selectionBoundaryEntry) return null;

    const [selectionBoundaryNode] = selectionBoundaryEntry;

    if (!isHeaderElement(selectionBoundaryNode)) return null;

    return selectionBoundaryNode.type;
  },

  /**
   * This method returns three ranges:
   *
   * 1. The range of the selection
   * 2. The range of the paragraphs before the selection
   * 3. The range of the paragraphs after the selection
   *
   * The options specify the number of paragraphs before and after the selection
   * to include in the surrounding context.
   *
   * NOTE: The end point of the `beforeRange` is the same as the start point
   * of the `range` and the start point of the `afterRange` is the same as the
   * end point of the `range`.
   */
  getRangeAndSurroundingParagraphRanges(
    editor: Editor,
    range: Range,
    options: { paragraphsAfter: number; paragraphsBefore: number }
  ): {
    range: Range;
    rangeAfter: Range;
    rangeBefore: Range;
  } {
    const { paragraphsAfter, paragraphsBefore } = options;
    const [rangeStart, rangeEnd] = Range.edges(range);
    const startParagraph = Editor.above(editor, {
      at: rangeStart,
      match: isParagraphElement,
    });
    const endParagraph = Editor.above(editor, {
      at: rangeEnd,
      match: isParagraphElement,
    });
    if (!startParagraph || !endParagraph) {
      return {
        range,
        rangeAfter: { anchor: rangeEnd, focus: rangeEnd },
        rangeBefore: { anchor: rangeStart, focus: rangeStart },
      };
    }

    // Build the before range starting from the start of the selection...
    const rangeBefore: Range = { anchor: rangeStart, focus: rangeStart };

    if (paragraphsBefore > 0) {
      let currentEntry = startParagraph;
      for (let i = 0; i < paragraphsBefore; i++) {
        const prevEntry = Editor.previous(editor, {
          at: currentEntry[1],
          match: isParagraphElement,
        });
        if (!prevEntry) break;

        rangeBefore.anchor = Editor.start(editor, prevEntry[1]);
        currentEntry = prevEntry;
      }
    }

    // Build the after range starting from the end of the selection...
    const rangeAfter: Range = { anchor: rangeEnd, focus: rangeEnd };

    if (paragraphsAfter > 0) {
      let currentEntry = endParagraph;
      for (let i = 0; i < paragraphsAfter; i++) {
        const nextEntry = Editor.next(editor, {
          at: currentEntry[1],
          match: isParagraphElement,
        });
        if (!nextEntry) break;

        rangeAfter.focus = Editor.end(editor, nextEntry[1]);
        currentEntry = nextEntry;
      }
    }

    return {
      range,
      rangeAfter,
      rangeBefore,
    };
  },

  getSelectablePoint(
    editor: Editor,
    {
      isDeletion = false,
      point,
      reverse = false,
    }: {
      point: Point;
      isDeletion?: boolean;
      reverse?: boolean;
    }
  ): Point {
    const safePoint = normalizePointToValid(editor, point);
    const unselectableEntry = Editor.above(editor, {
      at: safePoint,
      match: node => {
        if (!Element.isElement(node)) return false;
        if (!editor.isSelectable(node)) return true;
        // Do not select inside instruction fields when pressing backspace
        if (isDeletion && isInlineInstructionFieldElement(node)) return true;
        return false;
      },
    });

    // Points inside or before invisible elements are not valid
    if (
      Queries.isPointBeforeInvisible(editor, safePoint) ||
      (unselectableEntry && isInvisibleElement(unselectableEntry[0]))
    ) {
      // Editor.after doesn't always return the correct point in this case
      const nextPath = Path.next(unselectableEntry ? unselectableEntry[1] : safePoint.path);
      if (!Node.has(editor, nextPath)) return safePoint;
      const nextPoint = Editor.start(editor, nextPath);
      return Queries.getSelectablePoint(editor, { point: nextPoint, reverse });
    }

    // If the point is inside a non-first vertically merged cell, move it to the first cell
    const tableCellEntry = Editor.above(editor, { at: safePoint, match: isTableCellElement });
    if (tableCellEntry) {
      const [, vmCellStartPath] = Queries.getVerticallyMergedTableCellStart(editor, tableCellEntry);
      if (!Path.isDescendant(safePoint.path, vmCellStartPath))
        return reverse
          ? Editor.end(editor, vmCellStartPath)
          : Editor.start(editor, vmCellStartPath);
    }

    if (!unselectableEntry) return safePoint;

    const nextPoint = reverse ? Editor.before(editor, safePoint) : Editor.after(editor, safePoint);
    if (!nextPoint) return safePoint;

    return Queries.getSelectablePoint(editor, { isDeletion, point: nextPoint, reverse });
  },

  getSelectionIntersection<T extends BaseRange>(
    selection: T,
    range: Range
  ): (T & SelectionIntersection) | null {
    const intersection = Range.intersection(selection, range);
    if (!intersection) return null;

    // Range.intersection does not preserve the range direction
    const [start, end] = Range.edges(intersection);
    const [anchor, focus] = Range.isForward(selection) ? [start, end] : [end, start];

    const { isExpanded, isOriginalFocus: parentIsOriginalFocus } = isSelectionIntersection(
      selection
    )
      ? selection
      : { isExpanded: Range.isExpanded(selection), isOriginalFocus: true };

    const isOriginalFocus = parentIsOriginalFocus && Range.includes(range, selection.focus);
    return { ...selection, anchor, focus, isExpanded, isOriginalFocus };
  },

  getSelectionIntersections<T extends BaseRange>(
    parentSelections: Record<string, T> | null,
    range: Range
  ): Record<string, T & SelectionIntersection> | null {
    if (!parentSelections) return null;

    const selectionIntersections = flatMapObjectValues(parentSelections, parentSelection => {
      const intersection = Queries.getSelectionIntersection(parentSelection, range);
      return intersection ? [intersection] : [];
    });

    if (!Object.keys(selectionIntersections).length) return null;
    return selectionIntersections;
  },

  /**
   * Checks if the cursor is in a specific page footer
   */
  isSelectionInPageFooter(editor: Editor, footerType: FooterType): boolean {
    const selectionBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor);
    if (!selectionBoundaryEntry) return false;

    const [selectionBoundaryNode] = selectionBoundaryEntry;

    if (!isFooterElement(selectionBoundaryNode)) return false;

    return selectionBoundaryNode.type === footerType;
  },

  /**
   * Checks if the cursor is in a specific page header
   */
  isSelectionInPageHeader(editor: Editor, headerType: HeaderType): boolean {
    const selectionBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor);
    if (!selectionBoundaryEntry) return false;

    const [selectionBoundaryNode] = selectionBoundaryEntry;

    if (!isHeaderElement(selectionBoundaryNode)) return false;

    return selectionBoundaryNode.type === headerType;
  },

  /**
   * Checks if the current selection is on an even page
   */
  isSelectionOnEvenPageBody(editor: Editor): boolean {
    const currentPageNumber = Queries.getCurrentPageNumber(editor);
    if (!currentPageNumber) return false;
    return isEvenNum(currentPageNumber);
  },

  /**
   * Checks if the current selection is on the first page
   */
  isSelectionOnFirstPageBody(editor: Editor): boolean {
    const currentPageNumber = Queries.getCurrentPageNumber(editor);
    return currentPageNumber === 1;
  },

  /**
   * Checks if the current selection is on an odd page
   */
  isSelectionOnOddPageBody(editor: Editor): boolean {
    const currentPageNumber = Queries.getCurrentPageNumber(editor);
    if (!currentPageNumber) return false;
    return isOddNum(currentPageNumber);
  },
};

function normalizePointToValid(editor: Editor, point: Point): Point {
  const node = Node.getIf(editor, point.path);
  if (
    !node ||
    !Text.isText(node) ||
    point.offset < 0 ||
    (Text.isText(node) && point.offset > node.text.length)
  ) {
    console.error('normalizePointToValid: received invalid point', {
      offset: point.offset,
      path: point.path,
    });
  }

  if (node) {
    if (Text.isText(node)) {
      const offset = Math.min(Math.max(0, point.offset), node.text.length);
      return { offset, path: point.path };
    }
    return Editor.start(editor, point.path);
  }

  let path = point.path;
  while (path.length > 0 && !Node.has(editor, path)) {
    path = Path.parent(path);
  }

  if (path.length === 0) return Editor.start(editor, []);
  return Editor.start(editor, path);
}
